import {
    Feature,
    lineIntersect,
    lineString,
    Polygon as TurfPolygon,
    polygon,
    Properties,
    intersect,
    cleanCoords,
    buffer,
} from '@turf/turf'
import { ZoneVisibility } from '../types/polar-chart-types'

type Range = [number, number]
export type Polygons = number[][][]
type Polygon = number[][]

export type ZoneTypes =
    | 'safeZone'
    | 'heavyRollingZone'
    | 'parametricRollZone'
    | 'transverseAccelerationZone'
    | 'constraintZone'

export type BarChartZones = Array<{
    degree: number
    zones: TransformedZoneRange[]
}>
interface ZoneRange {
    type: ZoneTypes
    range: Range[]
}

interface TransformedZoneRange {
    type: ZoneTypes
    range: Range
}

interface ZonesData {
    heavyRollingZones: Range[]
    parametricRollZones?: Range[]
    transverseAccelerationZones?: Range[]
    constraintZones: Range[]
    safeZones?: Range[] // to be computed
}

// getting the final data for the bar chart
export const getBarChartZones = ({
    heavyRollingPolygons,
    parametricRollRiskPolygons,
    transverseAccelerationRiskPolygons,
    minSpeedPolygon,
    maxSpeedPolygon,
    startAngle = 0,
    endAngle = 360,
    increment = 5,
    criticalRollModel,
    zoneVisibility,
}: {
    heavyRollingPolygons?: Polygons
    parametricRollRiskPolygons?: Polygons
    transverseAccelerationRiskPolygons?: Polygons
    minSpeedPolygon: Polygon
    maxSpeedPolygon: Polygon
    startAngle?: number
    endAngle?: number
    increment?: number
    criticalRollModel?: string
    zoneVisibility?: ZoneVisibility
}): BarChartZones => {
    const { parametricRollPolygonsToRender, heavyRollingPolygonsToRender } =
        getRiskPolygonsToRender({
            heavyRollingPolygons,
            parametricRollRiskPolygons,
            criticalRollModel,
        })

    const heavyRollingCartesian =
        heavyRollingPolygonsToRender.length > 0
            ? getCartesianPolygon(heavyRollingPolygonsToRender)
            : []

    const parametricCartesian =
        parametricRollPolygonsToRender.length > 0
            ? getCartesianPolygon(parametricRollPolygonsToRender)
            : []

    const transverseAccelerationCartesian =
        transverseAccelerationRiskPolygons && transverseAccelerationRiskPolygons.length > 0
            ? getCartesianPolygon(transverseAccelerationRiskPolygons)
            : []

    const zones: BarChartZones = []

    for (let angle = startAngle; angle <= endAngle; angle += increment) {
        const constraintZones = getConstraintZones({
            minSpeedPolygon,
            maxSpeedPolygon,
            lineAngle: angle,
        })

        // Hidden zone types are masked to `[]` here (rather than filtered out
        // after stackedZones()) so the subtraction chain below naturally
        // cascades their reclaimed space to whichever visible zone is next in
        // precedence, instead of leaving a gap where they used to be.
        const heavyRollingZones = zoneVisibility?.heavyRollingZone === false
            ? []
            : getIntersections({
                lineAngle: angle,
                cartesianPolygons: heavyRollingCartesian,
            })

        const parametricRollZones = zoneVisibility?.parametricRollZone === false
            ? []
            : getIntersections({
                lineAngle: angle,
                cartesianPolygons: parametricCartesian,
            })

        const transverseAccelerationZones = zoneVisibility?.transverseAccelerationZone === false
            ? []
            : getIntersections({
                lineAngle: angle,
                cartesianPolygons: transverseAccelerationCartesian,
            })

        const combinedZones = stackedZones({
            heavyRollingZones,
            parametricRollZones,
            transverseAccelerationZones,
            constraintZones,
        })

        const transformedZones = transformZoneData(combinedZones)

        zones.push({
            degree: angle,
            zones: transformedZones,
        })
    }
    return zones
}

export const getRiskPolygonsToRender = ({
    heavyRollingPolygons,
    parametricRollRiskPolygons,
    criticalRollModel,
}: {
    heavyRollingPolygons?: Polygons
    parametricRollRiskPolygons?: Polygons
    criticalRollModel?: string
}): {
    heavyRollingPolygonsToRender: Polygons
    parametricRollPolygonsToRender: Polygons
} => {
    let parametricRollPolygonsToRender: Polygons = []
    let heavyRollingPolygonsToRender: Polygons = []

    if (criticalRollModel === 'TopTier') {
        parametricRollPolygonsToRender = parametricRollRiskPolygons || []
    } else if (heavyRollingPolygons && heavyRollingPolygons?.length > 0) {
        heavyRollingPolygonsToRender = heavyRollingPolygons
        // processing parametric roll polygons, and clipping where it overlaps with heavy rolling and use that for rendering
        parametricRollPolygonsToRender = parametricRollRiskPolygons
            ? clipParametricRollPolygons(
                  heavyRollingPolygons,
                  parametricRollRiskPolygons,
              )
            : []
    }
    return { parametricRollPolygonsToRender, heavyRollingPolygonsToRender }
}

// transforming data to a flat array of zone ranges for the bar chart to support echarts
function transformZoneData(input: ZoneRange[]) {
    const flattenedZones: TransformedZoneRange[] = []

    input.forEach((zone) => {
        zone.range.forEach((range) => {
            flattenedZones.push({
                type: zone.type,
                range: range,
            })
        })
    })

    return flattenedZones.sort((a, b) => a.range[0] - b.range[0])
}

// helper function to convert polar to cartesian coordinates
function polarToCartesian(r: number, thetaDeg: number) {
    const thetaRad = (thetaDeg * Math.PI) / 180 // Convert degrees to radians
    let x = r * Math.cos(thetaRad)
    let y = r * Math.sin(thetaRad)

    // Clean up floating-point errors - values very close to 0 should be exactly 0
    const epsilon = 1e-10
    if (Math.abs(x) < epsilon) x = 0
    if (Math.abs(y) < epsilon) y = 0

    return [x, y]
}

//capping it at 25 knots
function cartesianToKnotValue(x: number, y: number) {
    const knots = Math.sqrt(x ** 2 + y ** 2)
    return knots > 25 ? 25 : knots
}

// converting polar polygons to cartesian polygons
function getCartesianPolygon(
    polygons: Polygons,
): Feature<TurfPolygon, Properties>[] {
    return polygons.map((polygonPoints) => {
        const cartesianPoints = polygonPoints.map(([r, theta]) =>
            polarToCartesian(r, theta),
        )

        // Ensure the polygon is closed by checking if first and last points are the same
        // If not, explicitly add the first point at the end
        const firstPoint = cartesianPoints[0]
        const lastPoint = cartesianPoints[cartesianPoints.length - 1]

        if (firstPoint[0] !== lastPoint[0] || firstPoint[1] !== lastPoint[1]) {
            cartesianPoints.push([...firstPoint])
        }

        let poly = polygon([cartesianPoints])

        // Clean coordinates to remove duplicate points and fix topology issues
        poly = cleanCoords(poly)

        // Apply a tiny buffer to fix self-intersections and topology issues
        // This can help resolve floating-point precision problems
        try {
            const buffered = buffer(poly, 0, { units: 'meters' })
            if (buffered) {
                poly = buffered as Feature<TurfPolygon, Properties>
            }
        } catch (e) {
            // If buffering fails, continue with the cleaned polygon
            console.warn('Buffer operation failed, using cleaned polygon:', e)
        }

        return poly
    })
}

// calculating the intersection points for each polygon at provided angles
export const getIntersections = ({
    lineAngle,
    cartesianPolygons,
}: {
    lineAngle: number
    cartesianPolygons: Feature<TurfPolygon, Properties>[]
}): Range[] => {
    // radius (length of the line) - setting it to a higher number as some polygons go beyond the 25 range
    const lineRadius = 50
    const lineEndPoint = polarToCartesian(lineRadius, lineAngle)

    const line = lineString([[0, 0], lineEndPoint])

    const allRanges: Range[] = []

    cartesianPolygons.forEach((polygon) => {
        const intersections = lineIntersect(line, polygon)
        const intersectionPoints = intersections.features.map(
            (f) => f.geometry.coordinates,
        )

        let sortedValues = intersectionPoints
            .map(([x, y]) => cartesianToKnotValue(x, y))
            .filter((val) => val >= 0) // removing negative values
            .sort((a, b) => a - b) // sort remaining values - ascending

        // Remove duplicate values (within epsilon tolerance)
        // This handles tangent intersections or vertex intersections that produce duplicate points
        const epsilon = 0.001
        sortedValues = sortedValues.filter((val, idx, arr) => {
            if (idx === 0) return true
            return Math.abs(val - arr[idx - 1]) > epsilon
        })

        // If we have no valid intersections after deduplication, skip this polygon
        if (sortedValues.length === 0) {
            return
        }

        // Handle odd number of intersections: add 0 at the beginning
        // This happens when the polygon includes the origin
        if (sortedValues.length % 2 === 1) {
            sortedValues.unshift(0)
        }

        // Pair up the intersections into ranges: [entry, exit], [entry, exit], ...
        for (let i = 0; i < sortedValues.length; i += 2) {
            if (i + 1 < sortedValues.length) {
                allRanges.push([sortedValues[i], sortedValues[i + 1]])
            }
        }
    })

    return allRanges
}

// calculating the constraint zones for each angle
const getConstraintZones = ({
    minSpeedPolygon,
    maxSpeedPolygon,
    lineAngle,
}: {
    minSpeedPolygon: Polygon
    maxSpeedPolygon: Polygon
    lineAngle: number
}): Range[] => {
    const startPoint = minSpeedPolygon[lineAngle][0]
    const endPoint = maxSpeedPolygon[lineAngle][0]

    return [[startPoint, endPoint]]
}

// merges overlapping and adjacent ranges.
function mergeRanges(ranges: Range[]): Range[] {
    if (ranges.length === 0) return []

    // sort ranges based on start value
    const sorted = ranges.slice().sort((a, b) => a[0] - b[0])
    const merged: Range[] = [sorted[0]]

    for (let i = 1; i < sorted.length; i++) {
        const last = merged[merged.length - 1]
        const current = sorted[i]

        if (current[0] <= last[1]) {
            // overlapping or adjacent ranges
            merged[merged.length - 1] = [last[0], Math.max(last[1], current[1])]
        } else {
            merged.push(current)
        }
    }

    return merged
}

// subtracts a list of ranges from another list of ranges.
function subtractRanges(base: Range[], subtract: Range[]): Range[] {
    const result: Range[] = []
    const mergedSubtract = mergeRanges(subtract)

    base.forEach(([baseStart, baseEnd]) => {
        let tempStart = baseStart

        for (const [subStart, subEnd] of mergedSubtract) {
            if (subEnd <= tempStart) continue // No overlap
            if (subStart >= baseEnd) break // No further overlap

            if (subStart > tempStart) {
                result.push([tempStart, Math.min(subStart, baseEnd)])
            }

            tempStart = Math.max(tempStart, subEnd)

            if (tempStart >= baseEnd) break
        }

        if (tempStart < baseEnd) {
            result.push([tempStart, baseEnd])
        }
    })

    return result
}

//adjusts the zones by computing the final safe zones after accounting for danger and constraint zones.
function stackedZones(
    input: ZonesData,
    totalRange: Range = [0, 25],
): ZoneRange[] {
    // merging overlapping ranges within heavyRollingZones, parametricRollZones and constraintZones
    const mergedHeavyRollingZones = mergeRanges(input.heavyRollingZones)
    const mergedParametricRollZones = mergeRanges(
        input.parametricRollZones || [],
    )
    const mergedTransverseAccelerationZones = mergeRanges(
        input.transverseAccelerationZones || [],
    )
    const mergedConstraintZones = mergeRanges(input.constraintZones)

    // adjusting danger zones by precedence for stacked bar rendering
    const adjustedHeavyRollingZones = subtractRanges(
        mergedHeavyRollingZones,
        [
            ...mergedParametricRollZones,
            ...mergedTransverseAccelerationZones,
        ],
    )

    const adjustedParametricRollZones = subtractRanges(
        mergedParametricRollZones,
        mergedTransverseAccelerationZones,
    )

    // adjusting constraint zones by removing overlaps with Danger Zones
    const adjustedConstraintZones = subtractRanges(mergedConstraintZones, [
        ...adjustedHeavyRollingZones,
        ...adjustedParametricRollZones,
        ...mergedTransverseAccelerationZones,
    ])

    let safeZones: Range[] = [totalRange]

    // subtract heavy rolling zones from safe zones
    safeZones = subtractRanges(safeZones, adjustedHeavyRollingZones)

    // subtract parametric roll zones from safe zones
    safeZones = subtractRanges(safeZones, adjustedParametricRollZones)

    // subtract transverse acceleration zones from safe zones
    safeZones = subtractRanges(safeZones, mergedTransverseAccelerationZones)

    // subtract adjusted constraint zones from safe zones
    safeZones = subtractRanges(safeZones, adjustedConstraintZones)

    // merging safe zones
    safeZones = mergeRanges(safeZones)

    return [
        { type: 'safeZone', range: safeZones },
        { type: 'heavyRollingZone', range: adjustedHeavyRollingZones },
        { type: 'parametricRollZone', range: adjustedParametricRollZones },
        { type: 'transverseAccelerationZone', range: mergedTransverseAccelerationZones },
        { type: 'constraintZone', range: adjustedConstraintZones },
    ]
}

// Processes parametric roll polygons to separate overlapping regions
const clipParametricRollPolygons = (
    heavyRollingPolygons: Polygons,
    parametricRollPolygons: Polygons,
): Polygons => {
    if (!parametricRollPolygons || parametricRollPolygons.length === 0) {
        return []
    }

    const clippedParametricRollPolygons: Polygons = []

    try {
        // Convert all polygons to Turf format in Cartesian coordinates
        const heavyRollingCartesian = getCartesianPolygon(heavyRollingPolygons)
        const parametricCartesian = getCartesianPolygon(parametricRollPolygons)

        // For each heavy rolling polygon, check intersections with all parametric roll polygons
        heavyRollingCartesian.forEach((heavyPoly) => {
            parametricCartesian.forEach((paramPoly) => {
                try {
                    // Find intersection between heavy rolling and parametric roll
                    const intersection = intersect(heavyPoly, paramPoly)

                    if (intersection) {
                        // Handle both Polygon and MultiPolygon intersection results
                        const processIntersection = (coords: number[][]) => {
                            const polarIntersection = coords.map(([x, y]) => {
                                const r = Math.sqrt(x * x + y * y)
                                let theta = (Math.atan2(y, x) * 180) / Math.PI
                                if (theta < 0) theta += 360
                                return [r, theta]
                            })
                            clippedParametricRollPolygons.push(polarIntersection)
                        }

                        if (intersection.geometry.type === 'Polygon') {
                            processIntersection(
                                intersection.geometry.coordinates[0] as number[][],
                            )
                        } else if (intersection.geometry.type === 'MultiPolygon') {
                            // Handle multiple intersection polygons
                            intersection.geometry.coordinates.forEach(
                                (polyCoords: any) => {
                                    processIntersection(polyCoords[0] as number[][])
                                },
                            )
                        }
                    }
                } catch (error) {
                    // Skip this polygon pair if intersection calculation fails
                    console.warn('Failed to calculate intersection for polygon pair:', error)
                }
            })
        })
    } catch (error) {
        console.error('Error in clipParametricRollPolygons:', error)
        return []
    }

    return clippedParametricRollPolygons
}

// has to be this fornat for echarts
export const getArrowSVG = (color: string) => {
    const svg = `
        <svg width="10" height="17" viewBox="0 0 10 17" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M4.68765 16.202L0.101562 0.449219L4.54028 3.01915L9.15504 0.598322L4.68765 16.202Z" fill="${color}"/>
        </svg>
    `
    return 'data:image/svg+xml;base64,' + btoa(svg)
}
